"use client"

import OrgLayout from "@/layouts/OrgLayout"
import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Database, Download, History, Archive } from "lucide-react"
import { mockAuditLogs } from "@/lib/mock-org-data"

function OurData() {
  const [backupDialogOpen, setBackupDialogOpen] = useState(false)
  const [selectedBackupItems, setSelectedBackupItems] = useState({
    users: true,
    config: true,
    metadata: true,
    pdfs: true,
  })

  const recentBackups = [
    { id: "1", name: "Full Backup - Jan 10, 2025", date: "2025-01-10", size: "2.4 GB" },
    { id: "2", name: "Config Only - Jan 8, 2025", date: "2025-01-08", size: "12 MB" },
    { id: "3", name: "Full Backup - Jan 5, 2025", date: "2025-01-05", size: "2.3 GB" },
  ]

  const handleTriggerBackup = () => {

    setBackupDialogOpen(false)
  }

  return (
    <div className="space-y-6 w-full">
      <div>
        <h2 className="text-2xl font-semibold">Our Data</h2>
        <p className="text-sm text-muted-foreground">Manage backups, archives, and audit logs</p>
      </div>

      <div className="space-y-6">
        {/* Backups & Archives */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Database className="h-5 w-5 text-primary" />
              <div>
                <h3 className="font-semibold">Backups & Archives</h3>
                <p className="text-sm text-muted-foreground">Download recent backups or create a new one</p>
              </div>
            </div>
            <Dialog open={backupDialogOpen} onOpenChange={setBackupDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Archive className="h-4 w-4 mr-2" />
                  Full Backup
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Full Backup</DialogTitle>
                  <DialogDescription>Select what you want to include in the backup</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="backup-users"
                      checked={selectedBackupItems.users}
                      onCheckedChange={(checked) =>
                        setSelectedBackupItems({ ...selectedBackupItems, users: checked as boolean })
                      }
                    />
                    <Label htmlFor="backup-users" className="cursor-pointer">
                      User & Configuration Data
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="backup-metadata"
                      checked={selectedBackupItems.metadata}
                      onCheckedChange={(checked) =>
                        setSelectedBackupItems({ ...selectedBackupItems, metadata: checked as boolean })
                      }
                    />
                    <Label htmlFor="backup-metadata" className="cursor-pointer">
                      Fax Extraction Metadata
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="backup-pdfs"
                      checked={selectedBackupItems.pdfs}
                      onCheckedChange={(checked) =>
                        setSelectedBackupItems({ ...selectedBackupItems, pdfs: checked as boolean })
                      }
                    />
                    <Label htmlFor="backup-pdfs" className="cursor-pointer">
                      PDF Documents
                    </Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleTriggerBackup}>Create Backup</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-2">
            {recentBackups.map((backup) => (
              <div key={backup.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">{backup.name}</p>
                  <p className="text-sm text-muted-foreground">{backup.size}</p>
                </div>
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            ))}
          </div>
        </Card>

        {/* Audit Logs */}
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <History className="h-5 w-5 text-primary" />
            <div>
              <h3 className="font-semibold">Audit Logs</h3>
              <p className="text-sm text-muted-foreground">Track system activity and changes</p>
            </div>
          </div>

          <div className="space-y-2">
            {mockAuditLogs.map((log) => (
              <div key={log.id} className="flex items-start justify-between p-3 border rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium">{log.action}</p>
                  <p className="text-xs text-muted-foreground">
                    User ID: {log.user_id} • {new Date(log.created_at).toLocaleString()}
                  </p>
                </div>
                <span className="text-xs px-2 py-1 bg-secondary rounded-md">{log.entity_type}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  )
}
OurData.layout = (page: React.ReactNode) => <OrgLayout>{page}</OrgLayout>
export default OurData
